/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2019, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import $ from 'jquery';
import _ from 'underscore';
import StringResources from 'nls/StringResources';
const MULTI_MEASURES_SERIES = '_multiMeasuresSeries';
class ShowByAction {
	constructor(options) {
		this.content = options.content;
		this.content.getFeature('DataPointActions').registerProvider('ShowByAction', this);
		this.dashboardApi = options.dashboardAPI;
		this.visApi = null;
		this._showbyIdCntr = 0;
	}

	getAPI() {
		return {
			setOnSelectCallback: this.setOnSelectCallback.bind(this)
		};
	}

	setOnSelectCallback(onSelect) {
		this.onSelectCallback = onSelect.cb;
	}

	_resetVisInfo() {
		this.visApi = this.content.getFeature('WidgetAPI.deprecated').getVisApi();
		this._projectedDataItems = null;
		const visDefinition = this.visApi && this.visApi.getDefinition && this.visApi.getDefinition();
		this._isMapWidget = (visDefinition && visDefinition.geo);
		this._isCrosstabWidget = (visDefinition && visDefinition.id === 'crosstab');
		this._module = this.visApi.getModule();
		this.model = this.visApi.ownerWidget.model;
	}

	getDataPointActionList(selections, actionOptions) {
		if (this.dashboardApi.getApplicationName() !== 'explore') {
			return [];
		}
		// This class is constructed once but this method is called at each selection, so it's the good place to update the private members of this class at each user selection
		this._resetVisInfo();

		const sEventGroupId = this.visApi ? this.visApi.getEventGroupId() : null;
		const pageContextItems = this.visApi.getPageContextAPI().getPageContextItems ? this.visApi.getPageContextAPI().getPageContextItems({ scope: this.visApi.getScope(), origin: 'visualization', eventGroupId: sEventGroupId }) : [];
		const oSelectedItems = this._getBrushingItemThatMatchesSelection(pageContextItems);
		if (!oSelectedItems.length) {
			return [];
		}
		for (const selectedItem of oSelectedItems) {
			// disable showBy if any selected item has autogroup
			if (this._hasAutoGroupColumn(selectedItem)) {
				return [];
			}

			// disable showBy if any selected column is of type latitude or longitude as we can not map other geographical column like city, country, etc... to the latitude/longitude layer (defect 272143)
			if (this._hasLongitudeLatitudeColumn(selectedItem)) {
				return [];
			}
		}
		const setToolbar = this.setToolbar.bind(this);
		const viewOptions = this._getViewOptions(oSelectedItems, actionOptions.selections);
		if (!viewOptions) {
			// no candidate columns for showBy
			return [];
		}
		const nValueCount = this._getValueCountOfBrushingItems(oSelectedItems);
		this.compareByCase = nValueCount === 2;
		this.pageContextItems = oSelectedItems;

		const label = this.compareByCase ? StringResources.get('compare_by_label') : StringResources.get('show_by_label');
		// showBy is enabled when 1 or more than 3 data points are selected, otherwise enable compareBy
		return [{
			name: 'ShowByAction',
			label: label,
			text: label,
			type: 'NextView',
			order: 251, // as per design, this action is after 'Exclude', see dashboard-common/src/utils/ActionTypes.js for details.
			viewModule: 'explore/showByView',
			viewOptions: viewOptions,
			enableGoPrevious: false,
			setToolbar: setToolbar
		}];
	}

	_hasAutoGroupColumn(selectedItem) {
		const autoGroup = this.dashboardApi.getFeature('DataSources.CustomBinUI');
		const sourceId = selectedItem.getSourceId();
		const itemIds = selectedItem.getItemIds();
		if (sourceId && itemIds && itemIds.length) {
			return itemIds.some(itemId => {
				return !!autoGroup.getCustomBinning(sourceId, itemId);
			});
		}
		return false;
	}

	_hasLongitudeLatitudeColumn(selectedItem) {
		if (this._isMapWidget) {
			const itemIds = selectedItem.getItemIds();
			if (itemIds && itemIds.length) {
				return itemIds.some(itemId => {
					return this._getGeoTaxonomy(itemId).isLatLong;
				});
			}
			return false;
		}
	}

	/*
	* This function returns the value count of page context items generated by the selections
	* which helps to determine if showBy or compareBy should be enabled.
	* @return {number} valueCount - 2 means compareBy, other number means showBy
	* If the selection generates 1 page context item and it has:
	*	- 2 values => compareBy will be enabled
	*	- 1 or > 2 values => showBy will be enabled
	* If > 1 page context item (selection in Table):
	*	- enable compareBy only when there is only 1 page context item that has 2 values, all other items have 1 value (2 rows selected)
	*	- enable showBy for other cases
	*/
	_getValueCountOfBrushingItems(selections) {
		let nValueCount = null;
		if (selections.length === 1) {
			nValueCount = selections[0].getValueCount();
		} else {
			// case Table with many page context item entries
			const itemsWithMoreThanOneValue = selections.filter((selection) => {
				return selection.getValueCount() > 1;
			});
			if (itemsWithMoreThanOneValue.length === 1 && itemsWithMoreThanOneValue[0].getValueCount() === 2) {
				nValueCount = 2;
			} else {
				nValueCount = 1;
			}
		}
		return nValueCount;
	}

	/**
	* Method to set the toolbar so that we can remove the toolbar on certain actions
	* @param {Object} toolbar from which the action is called (i.e. the dialog opens from)
	*/
	setToolbar(toolbar) {
		this.toolbar = toolbar;
	}

	_getProjectedDataItems() {
		if (this._projectedDataItems && this._projectedDataItems.length) {
			return this._projectedDataItems;
		}
		const dataItemList = this.content.getFeature('Visualization').getSlots().getMappingInfoList().map( el => el.dataItem);
		this._projectedDataItems = dataItemList
			.filter(dataItem => dataItem.getColumnId() !== MULTI_MEASURES_SERIES)
			.map(dataItem => dataItem.getColumnId());
		return this._projectedDataItems;
	}

	/*
	* A selection contains 1 or more values of 1 or more itemIds
	* (eg: intersect Year and Country = 2 itemIds and a point like 2007-Canada would be 1 "value" of that intersection)
	* a brushing entry also contains a list of 1 or more itemIds and their values.
	* Usually the brushing and selections are the same but, for navigate, there are additional brushing entries .
	* Make sure we exclude brushing entries that are not visible and at the end, should have only 1 visible brushing entry before counting values.
	*/
	_getBrushingItemThatMatchesSelection(pageContextItems) {
		if (pageContextItems.length < 2) {
			return pageContextItems;
		}
		const visibleBrushingEntries = [];
		const projectedDataItems = this._getProjectedDataItems();
		pageContextItems.forEach(pageContextItem => {
			// this check is to work around the live widget issue where de-select a data point doesn't remove the page context entry,
			// it just removes all values of this entry
			if (pageContextItem.getValueCount && pageContextItem.getValueCount() > 0) {
				const itemIds = pageContextItem.getItemIds();
				if (_.intersection(projectedDataItems, itemIds).length === itemIds.length) {
					// All items in page context entry are projected
					visibleBrushingEntries.push(pageContextItem);
				}
			}
		});
		// For all vis types (except Table), should have only 1 visible brushing entry to be good candidate for showBy/compareBy.
		if (visibleBrushingEntries.length === 1) {
			return visibleBrushingEntries;
		} else {
			// For Table chart: selection of many columns in Table chart generates many page context item entries instead of dataPoint (behaviour since day 1 of gemini),
			// therefore it's still valid for showBy when there are many page context items generated by selection
			const visDefinition = this.visApi && this.visApi.getDefinition && this.visApi.getDefinition();
			const isTableWidget = (visDefinition && visDefinition.id === 'JQGrid');
			if (isTableWidget) {
				return visibleBrushingEntries;
			}
		}
		return [];
	}

	_getViewOptions(pageContextItems, selections) {

		this.slotAndDataItemsToRemap = this._getSlotAndDataItemsToRemap(pageContextItems, selections, this._isCrosstabWidget);
		if (!this.slotAndDataItemsToRemap) {
			return null;
		}
		const showByHandler = this.showByHandler.bind(this);
		const contextColumns = this._getCategoricalColumns();
		const shapingHelper = this._getShapingHelper();
		// do not show the showBy link if there are not other category columns to replace the selected one
		if (!contextColumns.length) {
			return null;
		}
		return {
			// height: 280, // TODO: remove and see if still works
			// width: 400, // TODO: remove and see if still works
			// state: this.state, //Default State // FIXME: this.state is nowhere
			eventHandler: showByHandler,
			widget: this.visApi.ownerWidget,
			slotId: this.slotAndDataItemsToRemap.slotId,
			closeOnActionApplied: true,
			contextColumns: contextColumns,
			shapingHelper: shapingHelper,
			getRecommendedColumns: this.getRecommendedColumns.bind(this)
		};
	}


	/*Get the slot ID and data items that the 'Show by Category' to replace with.
	* If the selection involves only 1 slot, return this slot and selected data items in this slot
	* If many slots are involved, choose 1 slot to replace:
	* 	- For crosstab: when user selects a cell value, both rows and columns slot are involved in the selection, but
	* it's more intuitive to replace "rows" slot than "columns" slot, so return "rows" slot and selected data items of this slot.
	* 	- For other chart types: we see this case for visualizations that also have data in color slot (ex: stack column chart with color data item)
	* in this case,  return the first slot (which is not supposed to be color slot in all visualization definitions) and selected data items
	* @param {Object} result - null or object containing slotId and array of its selected data items
	*/
	_getSlotAndDataItemsToRemap(pageContextItems, selections, crosstabWidget = false) {
		if (selections && selections._aCategorySelections && selections._aCategorySelections.length) {
			const slotToDataItems = selections._slotToCategoryCount;
			const slots = Object.keys(slotToDataItems);
			if (!slots.length) {
				return null;
			}
			let slotId = null;
			if (slots.length === 1 || (slots.length > 1 && !crosstabWidget)) {
				slotId = slots[0];
			} else {
				// crosstabWidget with both slots involved, return the "rows" slot which is the second slot
				slotId = slots[1];
			}
			let dataItemIds = [];
			pageContextItems.forEach((pageContextItem) => {
				dataItemIds = dataItemIds.concat(pageContextItem.getItemIds());
			});
			const dataItems = [];
			const _getDataItemsToRemap = () => {
				selections._aCategorySelections.forEach((selection) => {
					if (selection.slot.getId() === slotId) {
						const itemId = selection.slotDataItem.getColumnId();
						// make sure the data item is part of page context because this.selections._aCategorySelections is used for tooltip,
						// it can contain outer columns of table which are not part of the selection
						if (dataItemIds.indexOf(itemId) > -1) {
							dataItems.push({
								id: selection.slotDataItem.getId(),
								itemId: itemId
							});
						}
					}
				});
				return dataItems;
			};
			return {
				slotId: slotId,
				dataItems: _getDataItemsToRemap()
			};
		}
		return null;
	}

	showByHandler(payload) {
		let oPayload;
		if (payload) {
			oPayload = {
				showByCategory: {
					itemId: payload.context.id,
					caption: payload.context.label,
					id: ''
				},
				slotAndDataItemsToRemap: this.slotAndDataItemsToRemap,
				compareByCase: this.compareByCase,
				pageContextItems: this.pageContextItems
			};
		}
		this.onSelectCategory(oPayload);
	}

	/* Exclude columns that are already in projections of the visualization.
	* For map widget, only take "location" columns. If the selected column has taxonomy = 'location' ,
	* For other vis types, only take "attribute" columns.
	*/
	_isGoodCandidateColumnForShowBy(column) {
		if(!column){
			return false;
		}

		const columnId = column.getId();
		if(this._getProjectedDataItems().indexOf(columnId) > -1){
			return false;//Already projected
		}
		if(this._isMapWidget){
			return this._getGeoTaxonomy(columnId).isLocation;
		}else{
			return column.getType() === 'attribute';
		}
	}

	_getGeoTaxonomy(columnId) {
		const column = this.content.getFeature('Visualization').getDataSource().getMetadataColumn(columnId);
		let isLocation = false;
		let isLatLong = false;
		if (column) {
			const taxonomyList = column.getTaxonomyList();
			if (taxonomyList && taxonomyList.length) {
				const family = taxonomyList[0].getFamily();
				isLocation = taxonomyList[0].getClass() === 'cGeoLocation';
				isLatLong = family === 'cLatitude' || family === 'cLongitude';
			}
		}
		return { isLocation, isLatLong };
	}

	_getCategoryColumnOfKeyDriver(recommendedItem) {
		const column = this._module.getMetadataColumn(recommendedItem.fieldIDForExpression);
		if (this._isGoodCandidateColumnForShowBy(column)) {
			return column;
		}
		return null;
	}

	getRecommendedColumns() {
		const projectedDataItemIds = this._getProjectedDataItems();
		if (!projectedDataItemIds || projectedDataItemIds.length === 0) {
			return Promise.resolve([]);
		}
		let nbTarget = 0;
		const promises = [];
		const renderContext = {
			requestMap: {}
		};

		let visKeyDrivers = this.content.getFeature('VisKeyDrivers');
		visKeyDrivers = visKeyDrivers && visKeyDrivers.isVisKeyDriversFeatureEnabled() ? visKeyDrivers : null;

		projectedDataItemIds.forEach((projectedItemId) => {
			if (visKeyDrivers) {
				promises.push(visKeyDrivers.refreshPossibleKeyDrivers(projectedItemId, false /* useModelData */));
			} else {
				//TODO: this can be removed when the visKeyDrivers feature flag is removed.
				promises.push(this.visApi.whenPossibleKeyDriversAreReady(renderContext, projectedItemId, false /* useModelData */));
			}
		});

		return Promise.all(promises)
			.then((allKeyDrivers) => {
				const recommendedColumns = [];
				if (allKeyDrivers && allKeyDrivers.length) {
					allKeyDrivers.forEach((keyDrivers) => {
						if (keyDrivers && keyDrivers.recommendedDrivers && keyDrivers.recommendedDrivers.length) {
							nbTarget++;
							keyDrivers.recommendedDrivers.forEach((keyDriver) => {
								const column = this._getCategoryColumnOfKeyDriver(keyDriver);
								if (column) {
									recommendedColumns.push({
										id: column.getId(),
										label: column.getLabel(),
										moserObject: column.getMoserObject()
									});
								}
							});
						}
					});
				}
				return recommendedColumns;
			})
			.then((recommendedColumns) => {
				if (nbTarget > 1) {
					// need to remove duplicate columns in the result
					recommendedColumns = _.uniq(recommendedColumns, function (column) {
						return column.id;
					});
				}
				return recommendedColumns;
			});
	}

	_getCategoricalColumns() {
		const columns = [];
		const filterColumnsCallBack = (column) => {
			return this._isGoodCandidateColumnForShowBy(column);
		};
		const metadataColumns = this._module.getMetadataColumns(filterColumnsCallBack);
		if (metadataColumns && metadataColumns.length) {
			metadataColumns.forEach((column) => {
				const idForExpression = column.getId();
				columns.push({
					value: idForExpression,
					label: column.getLabel(),
					title: idForExpression, // tooltip,
					moserObject: column.getMoserObject()
				});
			});
		}
		return columns;
	}

	_getShapingHelper() {
		if (this.isNewShowCompareByEnabled()) {
			return this._module.getShapingHelper();
		}
		return null;
	}

	isNewShowCompareByEnabled() {
		if (this.dashboardApi) {
			// Access featureChecker to check feature flag for new edit drivers dialog
			const featureChecker = this.dashboardApi && this.dashboardApi.getGlassCoreSvc('.FeatureChecker');
			return !(featureChecker && featureChecker.checkValue('explore', 'newShowCompareBy', 'false'));
		}
		return false;
	}

	onSelectCategory(params) {
		// The onSelectCallback of showBy is in ExploreCard.addShowByCard
		if (this.onSelectCallback) {
			// If transactionId is not passed by caller, start it here and pass it to Explore callback function
			params.payloadOptions = params.payloadOptions || {};
			params.payloadOptions.payloadData = params.payloadOptions.payloadData || {};
			params.payloadOptions.payloadData.undoRedoTransactionId = params.payloadOptions.payloadData.undoRedoTransactionId || _.uniqueId('addShowByCard');
			this.onSelectCallback(this.getNewWidgetSpecs(params), params.payloadOptions.payloadData);
		}
	}

	getNewWidgetSpecs(params) {
		if (!params || !params.pageContextItems) {
			return [];
		}
		this._showbyIdCntr = this._showbyIdCntr || 0;
		params.showByCategory.id = this.visApi.ownerWidget.id + '_showBy_' + ++this._showbyIdCntr;
		let specs = [];
		if (params.compareByCase) {
			specs = this._getWidgetSpecsForCompareBy(params);
		} else {
			specs = this._getWidgetSpecsForShowBy(params);
		}
		// remove visualizationId in filter spec so that dashboard won't treat these filters as brushed filters
		// (dashbpard live-widget relies on this property to distinguish filter for brushed item and local filter)
		specs.forEach(spec => {
			if (spec.localFilters && spec.localFilters.length) {
				spec.localFilters.forEach(filter => {
					filter.visualizationId = undefined;
				});
			}
		});
		return specs;
	}

	_getWidgetSpecsForShowBy(params) {
		var specs = [];
		/*For showBy, set the option to exclude visible selections to false because
		 the visible selections form part of the showBy context */
		const filters = this.visApi.getAllFiltersAsLocalFiltersForPinning(/*excludeVisibleSelections*/ false);
		var widgetSpec = this.newWidgetSpecWithFilter(params, filters, this.model);
		// widget id, name and titleHtml should be cleared in order to be regenerated
		widgetSpec = this.removeUnwantedAttributesFromSpec(widgetSpec);
		widgetSpec && specs.push(widgetSpec);
		this.clearSelections(params.payloadOptions);
		return specs;
	}

	_getWidgetSpecsForCompareBy(params) {
		const filterCount = params.pageContextItems.length;
		const filter1 = this.visApi.getSelectionAsLocalFilters({ includeLocalFilters: true });
		const filter2 = this.visApi.getSelectionAsLocalFilters({ includeLocalFilters: true });
		const nbFilters = filter1.length;
		let i = nbFilters - filterCount;
		while (i < nbFilters) {
			if (filter1[i].values.length === 2) {
				filter1[i].values.splice(1, 1);
				filter2[i].values.splice(0, 1);
			}
			i++;
		}

		var selectionAsFilters = [filter1, filter2];
		var specs = [];
		var widgetSpec = this.newWidgetSpecWithFilter(params, selectionAsFilters[0], this.model);
		widgetSpec = this._addExtraWidgetOptions(widgetSpec);
		// widget id, name and titleHtml should be cleared in order to be regenerated
		widgetSpec = this.removeUnwantedAttributesFromSpec(widgetSpec);
		widgetSpec && specs.push(widgetSpec);
		widgetSpec = this.newWidgetSpecWithFilter(params, selectionAsFilters[1], this.model);
		widgetSpec = this._addExtraWidgetOptions(widgetSpec);
		// widget id, name and titleHtml should be cleared in order to be regenerated
		widgetSpec = this.removeUnwantedAttributesFromSpec(widgetSpec);
		widgetSpec && specs.push(widgetSpec);
		this.clearSelections(params.payloadOptions);
		return specs;
	}

	clearSelections(options) {
		this.visApi.clearSelectionsUsedForShowBy(options);
	}

	newDataItem(showByCategory) {
		return {
			id: showByCategory.id,
			itemId: showByCategory.itemId,
			itemLabel: showByCategory.caption
		};
	}

	addDataItem(widgetModelClone, params) {
		if (!widgetModelClone || !widgetModelClone.data || !widgetModelClone.data.dataViews
			|| widgetModelClone.data.dataViews.length === 0) {
			return null;
		}
		var dataItem = this.newDataItem(params.showByCategory);
		widgetModelClone.data.dataViews[0].dataItems.push(dataItem);
		return dataItem;
	}

	updateSlot(widgetModelClone, params, newDataItemUniqueId) {
		if (!widgetModelClone || !widgetModelClone.slotmapping || !widgetModelClone.slotmapping.slots) {
			return null;
		}
		var slotToRemap = null;
		let dataItemIdsToReplace = [];
		widgetModelClone.slotmapping.slots.some(function (slot) {
			if (slot.name === params.slotAndDataItemsToRemap.slotId) {
				slotToRemap = slot;
				dataItemIdsToReplace = params.slotAndDataItemsToRemap.dataItems.map(dataItem => dataItem.id);
				return true;
			}
			return false;
		});
		if (slotToRemap) {
			if (widgetModelClone.visId === 'JQGrid' || widgetModelClone.visId === 'crosstab') {
				this.updateSlotForCrosstabAndTable(slotToRemap, dataItemIdsToReplace, newDataItemUniqueId);
				this.removeHeatSlotForXtabTable(widgetModelClone);
			} else {
				slotToRemap.dataItems = [newDataItemUniqueId];
				slotToRemap.caption = params.showByCategory.caption;
			}
		}
		if (dataItemIdsToReplace.length) {
			this._removeBrushedItems(widgetModelClone, dataItemIdsToReplace);
		}
		return slotToRemap;
	}

	/*
	* For crosstab and table visualization, we don't replace all columns in the slot, we just replace the selected columns in the slot with the new column.
	* For Table, if there are measures between the replaced columns, make sure we insert the new data item before the measure columns.
	* Ex: table projections are: [cat1, measure1, cat2] and selection contains cat1 and cat2.
	* After updateSlot, projections should become: [newColumn, measure1]
	*/
	updateSlotForCrosstabAndTable(slotToRemap, dataItemIdsToReplace, newDataItemUniqueId) {
		const newDataItems = [];
		let replaceIndex = -1;
		for (const i in slotToRemap.dataItems) {
			if (dataItemIdsToReplace.indexOf(slotToRemap.dataItems[i]) === -1) {
				newDataItems.push(slotToRemap.dataItems[i]);
			} else if (replaceIndex === -1) {
				// replace
				newDataItems.push(newDataItemUniqueId);
				replaceIndex = newDataItems.length - 1;
			}
		}
		slotToRemap.dataItems = newDataItems;
	}

	newWidgetSpecWithFilter(params, selectionFilter, model) {
		var modelJSON = model.toJSON();

		var modelJSONClone = $.extend(true, {}, modelJSON);
		delete modelJSONClone.containerPageId;

		var dataItem = this.addDataItem(modelJSONClone, params);

		const updateDone = dataItem && this.updateSlot(modelJSONClone, params, dataItem.id);

		if (updateDone) {
			modelJSONClone.localFilters = selectionFilter;
			return modelJSONClone;
		} else {
			return null;
		}
	}

	/* 
	* If the vis type is Table/xtab, remove the heat slot and data items associated 
	* with heat as we don't carry over the new CF properties in R7 for showBy/compareBy
	*/
	removeHeatSlotForXtabTable(widgetSpec) {
		const newConditionalFormattingEnabled = !(this.dashboardApi.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'xtabcondFormat', 'disabled'));
		if (newConditionalFormattingEnabled) {
			const visType = this.content.getFeature('Visualization').getType();
			if (['Crosstab', 'List'].indexOf(visType) > -1) {
				const slots = widgetSpec.slotmapping.slots;
				const dataView = widgetSpec.data.dataViews[0];
				let heatIndex = null;
				let dataItemsInHeat;
				for (let i = 0; i < slots.length; ++i) {
					if (slots[i].name === 'heat') {
						heatIndex = i;
						dataItemsInHeat = slots[i].dataItems;
						break;
					}
				}
				if (heatIndex !== null) {
					slots.splice(heatIndex, 1);
					const dataItems = dataView.dataItems;
					const preservedDataItems = [];
					for (let i = 0; i < dataItems.length; ++i) {
						if (dataItemsInHeat.indexOf(dataItems[i].id) === -1) {
							// keep data items that are not in heat slot
							preservedDataItems.push(dataItems[i]);
						}
					}
					if (preservedDataItems.length) {
						dataView.dataItems = preservedDataItems;
					}
				}
			}
		}
		return widgetSpec;
	}

	_removeBrushedItems(widgetModel, dataItemIdsToReplace){
		const dataViews = widgetModel.data.dataViews;
		for(let i=0; i<dataViews.length; i++){
			this._removeBrushedDataItemFromDataViews(dataViews[i], dataItemIdsToReplace);
		}
	}

	_removeBrushedDataItemFromDataViews(dataView, dataItemIdsToReplace){
		const updatedDataItems = [];
		const dataItems = dataView.dataItems;
		for(let i=0; i<dataItems.length; i++){
			if( dataItemIdsToReplace.indexOf(dataItems[i].id) === -1 ){
				updatedDataItems.push(dataItems[i]);
			}
		}

		if(updatedDataItems.length > 0){
			dataView.dataItems = updatedDataItems;
		}
	}

	/* Remove old unwanted attributes from the spec so as to allow the auto-generation
	of new and accurate attributes for the widget i.e. Title or id */
	removeUnwantedAttributesFromSpec(widgetSpec) {
		var unwantedAttrs = ['id', 'name', 'titleHtml', 'thumbnailId'];
		_.each(unwantedAttrs, function (attr) {
			delete widgetSpec[attr];
		});
		return widgetSpec;
	}

	_addExtraWidgetOptions(widgetSpec) {
		const extraWidgetOptions = {
			properties: [{
				id: 'itemAxis.labels.layoutMode',
				value: 'stagger'
			}, {
				id: 'widget.legend.position',
				value: 'right'
			}]
		};
		Object.assign(widgetSpec, extraWidgetOptions);
		return widgetSpec;
	}
}

export default ShowByAction;



// WEBPACK FOOTER //
// ./src/js/features/showByCompareBy/ShowByAction.js